import Collapsible from '@react/search/Filters/Collapsible';
import FilterButton from '@react/search/Filters/FilterButton';
import FilterChoice from '@react/search/Filters/FilterChoice';
import { useBreakpoint } from '@react/services/breakpoint';
import { eventNames, useEvents } from '@react/services/events';
import Modal, {
  HeaderWithClose,
  ModalActions,
  ModalBody,
} from '@react/shared/components/NewModal';
import Popover from '@react/shared/components/Popover';
import { useLiveDimensions } from '@react/shared/hooks/useLiveDimensions';
import {
  ALL_FILTERS,
  // BUDGET,
  // BUDGETS,
  COMMUNITY_AMENITIES,
  COMMUNITY_SERVICES,
  DINING_SERVICES,
  getCtaEvent,
  HEALTH_CARE_SERVICES,
  MORE_FILTERS,
  PAGINATION_FILTERS,
  ROOM_AMENITIES,
  SIZE,
  SIZES,
  SL,
  STARTING_RATE,
  STARTING_RATES,
  TOC,
  TOCS,
} from '@sly/core/constants/filters';
import {
  amenitiesAndServiceTitles,
  amenitiesAndServiceTypes,
} from '@sly/core/src/constants/amenitiesAndServices';
import cx from 'classnames';
import { bool, func, node, object, string } from 'proptypes';
import React, { forwardRef, useCallback, useMemo, useState } from 'react';
import { Button } from 'sly/system';

import { getMoreFiltersOptions } from '../helpers';

const TOC_OPTIONS = Object.values(TOCS);
const SIZE_OPTIONS = Object.values(SIZES);
// const BUDGET_OPTIONS = Object.values(BUDGETS);
const STARTING_RATE_OPTIONS = Object.values(STARTING_RATES);

const countFilters = (currentFilters, list) =>
  Object.entries(currentFilters)
    .filter(([key]) => list.includes(key))
    .reduce((acc, [key, value]) => {
      if (key === TOC && value === SL) {
        return acc;
      }
      return Array.isArray(value)
        ? acc + value.length
        : // eslint-disable-next-line no-mixed-operators
          (value && acc + 1) || acc;
    }, 0);

const CollapsiblePopoverSwitch = ({
  isPopOver,
  showIf,
  children,
  ...props
}) => {
  if (!showIf) {
    return null;
  }
  if (isPopOver) {
    return <div className='pt-l'>{children}</div>;
  }
  return (
    <div className='border-b-[1px]'>
      <Collapsible {...props}>{children}</Collapsible>
    </div>
  );
};

CollapsiblePopoverSwitch.propTypes = {
  title: string,
  isPopOver: bool,
  showIf: bool,
  children: node,
};

const ModalPopoverSwitch = ({ isPopOver, children, ...props }) => {
  const Component = isPopOver ? Popover : Modal;
  return <Component {...props}>{children}</Component>;
};

ModalPopoverSwitch.propTypes = {
  isPopOver: bool,
  children: node,
};

const Filters = forwardRef(
  (
    {
      isOpen: defaultIsOpen,
      onFilterChange,
      onClearFilters,
      currentFilters,
      showTOC,
      children,
      query,
      ...props
    },
    ref
  ) => {
    const { events } = useEvents();

    const sendEvent = (text, cta, internalQuery, location = 'filter bar') =>
      events.track(eventNames.ButtonClicked, {
        text,
        cta,
        query: internalQuery,
        location,
      });

    const [isOpen, setIsOpen] = useState(defaultIsOpen || false);
    const closeModal = useCallback(
      () =>
        sendEvent('X', 'Close modal', query, 'filter modal') ||
        setIsOpen(false),
      [isOpen]
    );
    const clearFilters = useCallback(() => {
      sendEvent('Clear all', 'Clear all filters', query, 'filter modal');
      typeof isOpen === 'string'
        ? onClearFilters([...PAGINATION_FILTERS, isOpen])
        : onClearFilters([...PAGINATION_FILTERS, ...isOpen]);
    }, [isOpen]);
    const breakpoint = useBreakpoint();
    const showIf = useCallback(
      (type) => Boolean(isOpen === type || (breakpoint?.isMobile() && isOpen)),
      [isOpen, breakpoint]
    );
    const [sizeButtonRef, sizeButtonCoords] = useLiveDimensions({
      liveMeasure: true,
    });
    const [priceButtonRef, priceButtonCoords] = useLiveDimensions({
      liveMeasure: true,
    });

    const popOverCss = useMemo(() => {
      if (
        breakpoint?.atLeastTablet() &&
        [STARTING_RATE, SIZE].includes(isOpen)
      ) {
        const coords = {
          [STARTING_RATE]: priceButtonCoords,
          [SIZE]: sizeButtonCoords,
        }[isOpen];
        return {
          position: 'fixed',
          top: coords.top + coords.height + 5,
          left: coords.left,
        };
      }
      return null;
    }, [breakpoint, isOpen, priceButtonCoords, sizeButtonCoords]);

    const title = useMemo(() => {
      if (breakpoint?.isMobile()) {
        return 'Filters';
      }
      switch (isOpen) {
        case TOC:
          return 'Filter by community type';
        case MORE_FILTERS:
          return 'More filters';
        default:
          return null;
      }
    }, [breakpoint, isOpen]);

    const onTocFilterChange = useCallback(
      (filter, value) => {
        onFilterChange(filter, value.length === 0 ? [SL] : value);
      },
      [currentFilters]
    );
    const onBudgetFilterChange = useCallback(
      (filter, value) => {
        onFilterChange(filter, value.length === 0 ? null : value);
      },
      [currentFilters]
    );

    const createOpenFilters =
      (section = true) =>
      (e) => {
        e.stopPropagation();
        let sectionName = section.toString();
        if (typeof section === 'object') {
          sectionName = 'More filters';
        }
        sendEvent(sectionName, getCtaEvent(section.toString()), query);
        isOpen !== section ? setIsOpen(section) : setIsOpen(false);
      };

    const disableMoreFiltersCollapse =
      showIf(MORE_FILTERS) && breakpoint?.atLeastTablet();

    const totalNumberOfFilters = countFilters(currentFilters, ALL_FILTERS);
    const totalMoreFilters = countFilters(currentFilters, MORE_FILTERS);
    const currentTocText =
      currentFilters[TOC] === 'care-home'
        ? ''
        : TOCS[currentFilters[TOC]]?.label;
    const currentSizeText = SIZES[currentFilters[SIZE]]?.label;
    const currentBudgetText =
      STARTING_RATES[currentFilters[STARTING_RATE]]?.label;

    return (
      <>
        <ModalPopoverSwitch
          isOpen={!!isOpen}
          isPopOver={!!popOverCss}
          style={{ ...popOverCss }}
          onClose={closeModal}
        >
          {title && (
            <HeaderWithClose onClose={closeModal}>{title}</HeaderWithClose>
          )}
          <ModalBody className='overflow-y-auto px-l py-0'>
            {showTOC && (
              <CollapsiblePopoverSwitch
                isPopOver={breakpoint?.atLeastTablet()}
                showIf={showIf(TOC)}
                title='Type of community'
              >
                <FilterChoice
                  type='radio'
                  options={TOC_OPTIONS}
                  filter={TOC}
                  onChange={onTocFilterChange}
                  value={currentFilters[TOC]}
                />
              </CollapsiblePopoverSwitch>
            )}

            <CollapsiblePopoverSwitch
              isPopOver={!!popOverCss}
              showIf={showIf(SIZE)}
              title='Size'
            >
              <FilterChoice
                type='radio'
                options={SIZE_OPTIONS}
                filter={SIZE}
                onChange={onFilterChange}
                value={currentFilters[SIZE]}
              />
            </CollapsiblePopoverSwitch>
            <CollapsiblePopoverSwitch
              isPopOver={!!popOverCss}
              showIf={showIf(STARTING_RATE)}
              title='Price'
            >
              <FilterChoice
                type='radio'
                options={STARTING_RATE_OPTIONS}
                filter={STARTING_RATE}
                onChange={onBudgetFilterChange}
                value={currentFilters[STARTING_RATE]}
              />
            </CollapsiblePopoverSwitch>
            <Collapsible
              showIf={showIf(MORE_FILTERS)}
              title={amenitiesAndServiceTitles.healthCareService}
              data-testid='CareServicesFilter'
              disabled={disableMoreFiltersCollapse}
            >
              <FilterChoice
                type='checkbox'
                options={getMoreFiltersOptions(
                  amenitiesAndServiceTypes.healthCareService
                )}
                filter={HEALTH_CARE_SERVICES}
                onChange={onFilterChange}
                value={currentFilters[HEALTH_CARE_SERVICES]}
              />
            </Collapsible>
            <Collapsible
              showIf={showIf(MORE_FILTERS)}
              title={amenitiesAndServiceTitles.communityService}
              data-testid='CommunityServicesFilter'
              disabled={disableMoreFiltersCollapse}
            >
              <FilterChoice
                type='checkbox'
                options={getMoreFiltersOptions(
                  amenitiesAndServiceTypes.communityService
                )}
                filter={COMMUNITY_SERVICES}
                onChange={onFilterChange}
                value={currentFilters[COMMUNITY_SERVICES]}
              />
            </Collapsible>
            <Collapsible
              showIf={showIf(MORE_FILTERS)}
              title={amenitiesAndServiceTitles.roomAmenities}
              data-testid='RoomAmenitiesFilter'
              disabled={disableMoreFiltersCollapse}
            >
              <FilterChoice
                type='checkbox'
                options={getMoreFiltersOptions(
                  amenitiesAndServiceTypes.roomAmenities
                )}
                filter={ROOM_AMENITIES}
                onChange={onFilterChange}
                value={currentFilters[ROOM_AMENITIES]}
              />
            </Collapsible>
            <Collapsible
              showIf={showIf(MORE_FILTERS)}
              title={amenitiesAndServiceTitles.communityAmenities}
              borderBottom='none'
              data-testid='CommunityAmenitiesFilter'
              disabled={disableMoreFiltersCollapse}
            >
              <FilterChoice
                type='checkbox'
                options={getMoreFiltersOptions(
                  amenitiesAndServiceTypes.communityAmenities
                )}
                filter={COMMUNITY_AMENITIES}
                onChange={onFilterChange}
                value={currentFilters[COMMUNITY_AMENITIES]}
              />
            </Collapsible>
            <Collapsible
              showIf={showIf(MORE_FILTERS)}
              title={amenitiesAndServiceTitles.diningService}
              borderBottom='none'
              data-testid='CommunityDiningFilter'
              disabled={disableMoreFiltersCollapse}
            >
              <FilterChoice
                type='checkbox'
                options={getMoreFiltersOptions(
                  amenitiesAndServiceTypes.diningService
                )}
                filter={DINING_SERVICES}
                onChange={onFilterChange}
                value={currentFilters[DINING_SERVICES]}
              />
            </Collapsible>
          </ModalBody>
          <ModalActions>
            <Button
              variant='custom'
              className='font-b-s border-0 px-m text-viridian-base'
              onClick={clearFilters}
              disabled={totalNumberOfFilters === 0}
              data-testid='ClearAllButton'
            >
              Clear all
            </Button>
            <Button
              className='font-b-s ml-auto'
              onClick={closeModal}
              data-testid='SaveButton'
            >
              Save
            </Button>
          </ModalActions>
        </ModalPopoverSwitch>
        <div
          ref={ref}
          className={cx(
            'sticky flex flex-col bg-white',
            'top-[6.75rem] z-[999] border-b-[1px] border-slate-lighter-90 py-m px-l md:top-[4.5rem] lg:top-[7.5rem]',
            'gridAreaFilter'
          )}
          // gridArea="filters"
          {...props}
          data-tp-id='gen-div-c406dc3e-3a5b-489a-be34-e8655bd118eb'
        >
          <div className='flex flex-row'>
            <div className='md:hidden'>
              <FilterButton
                className='flex'
                onClick={createOpenFilters(ALL_FILTERS)}
                number={totalNumberOfFilters}
                data-testid='mobileFiltersButton'
              >
                Filters
              </FilterButton>
            </div>
            {showTOC && (
              <FilterButton
                className='hidden md:flex'
                onClick={createOpenFilters(TOC)}
                selected={Boolean(currentTocText)}
                data-testid='TocFilter'
              >
                {currentTocText || 'Community type'}
              </FilterButton>
            )}
            <FilterButton
              ref={sizeButtonRef}
              className='hidden md:flex'
              onClick={createOpenFilters(SIZE)}
              selected={Boolean(currentSizeText)}
              data-testid='SizeFilter'
            >
              {currentSizeText || 'Size'}
            </FilterButton>
            <FilterButton
              ref={priceButtonRef}
              className='hidden md:flex'
              onClick={createOpenFilters(STARTING_RATE)}
              selected={Boolean(currentBudgetText)}
              data-testid='PriceFilter'
            >
              {currentBudgetText || 'Price'}
            </FilterButton>
            <FilterButton
              className='hidden md:flex'
              onClick={createOpenFilters(MORE_FILTERS)}
              number={totalMoreFilters}
              data-testid='MoreFilter'
            >
              More filters
            </FilterButton>
            {children}
          </div>
        </div>
      </>
    );
  }
);

Filters.propTypes = {
  isOpen: bool,
  showTOC: bool,
  nextShow: string,
  toggleShow: func,
  onFilterChange: func,
  onClearFilters: func,
  currentFilters: object,
  children: node,
  query: string,
};

export default Filters;
